import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { VALIDATION_POLL_INTERVAL_MS } from '~constants/common';

import { Button, ContainerLoading, SummaryStatCard } from '~components';
import Icon, { faCheckCircle } from '~components/icon';
import { UserFeedback } from '~main/components';

import { Claim } from '~support-at-home/models';

import { useCreateClaimMutation, useGetClaimQuery } from '~support-at-home/actions/claims';

interface ComponentProps {
  value: {
    start_date: string;
    end_date: string;
    naps_id?: string[];
    status_id?: string[];
    service_type_id?: string[];
  };
}

const ClaimDetailsNew: React.FC<ComponentProps> = ({ value }) => {
  const { start_date, end_date, naps_id, status_id, service_type_id } = value;
  const navigate = useNavigate();

  const [createdClaimId, setCreatedClaimId] = useState<string | null>(null);
  const [initialClaim, setInitialClaim] = useState<Claim | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [createClaim, { isLoading }] = useCreateClaimMutation();

  // Poll the created claim directly to detect validation completion
  const { data: fetchedClaim, refetch: refetchClaim } = useGetClaimQuery(
    { id: createdClaimId ?? '' },
    { skip: !createdClaimId },
  );

  // Poll while validating
  const prevIsValidatingRef = useRef(fetchedClaim?.is_validating);
  useEffect(() => {
    prevIsValidatingRef.current = fetchedClaim?.is_validating;

    if (!fetchedClaim?.is_validating) return;

    const interval = setInterval(() => {
      refetchClaim();
    }, VALIDATION_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchedClaim?.is_validating, refetchClaim]);

  const claim = fetchedClaim ?? initialClaim;
  const isValidating = claim?.is_validating ?? false;

  const itemsWithErrors = claim?.items_with_errors ?? 0;
  const itemsValid = claim?.items_valid ?? 0;

  useEffect(() => {
    if (loaded) return;
    create();
  }, [start_date, end_date, naps_id, status_id, service_type_id, loaded]);

  const create = async () => {
    try {
      const { data: payload } = await createClaim({ start_date, end_date, naps_id, status_id, service_type_id });
      const { claim } = payload ?? {};
      if (!claim) return;

      setInitialClaim(claim);
      setCreatedClaimId(claim.id);
      setLoaded(true);
    } catch (error) {
      console.warn('error on create', error);
    }
  };

  const handleNavigateToClaim = () => {
    if (claim?.id) {
      navigate(`/support-at-home/claims/${claim.id}`);
    }
  };

  return (
    <ContainerLoading loading={isLoading} bgColor="white" color="black" isTransparent={true}>
      {claim && (
        <div className="d-flex flex-column align-items-center py-5">
          <div className="text-center mb-4">
            <div className="mb-3">
              <Icon icon={faCheckCircle} size="4x" className="text-success" />
            </div>
            <h3 className="fw-bold text-success mb-2">Claim Created Successfully!</h3>
            <p className="text-muted">Reference: {claim.reference}</p>
          </div>

          <div className="row w-100 mb-4 gap-3 justify-content-center">
            <div className="col-md-2">
              <SummaryStatCard count={claim.items_count ?? 0} countLabel="Total Claim Items" />
            </div>
            <div className="col-md-2">
              <SummaryStatCard
                count={itemsValid}
                countLabel="Valid Items"
                countClassName={'text-success'}
                isLoading={isValidating}
              />
            </div>
            <div className="col-md-2">
              <SummaryStatCard
                count={itemsWithErrors}
                countLabel="Items with Errors"
                countClassName={'text-danger'}
                isLoading={isValidating}
              />
            </div>
            <div className="col-md-2">
              <SummaryStatCard count={claim.items_ignored ?? 0} countLabel="Ignored Items" />
            </div>
          </div>
          {isValidating && <p className="text-muted mb-4">Claim validation in progress...</p>}

          <Button className={'text-white'} color="success" size="lg" onClick={handleNavigateToClaim}>
            View Claim
          </Button>
        </div>
      )}
      <UserFeedback workflowType="sah-claims-wizard" isVisible={!isLoading && !!claim} />
    </ContainerLoading>
  );
};

export default ClaimDetailsNew;
